import React, {useState, useRef, useCallback} from 'react';
import {
  View, FlatList, StyleSheet, TouchableOpacity, ScrollView, RefreshControl, Image,
} from 'react-native';
import {Text, Appbar, ActivityIndicator, Snackbar} from 'react-native-paper';
import {useNavigation, DrawerActions, useFocusEffect} from '@react-navigation/native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import {taskApi} from '../../services/api/taskApi';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import OrgUnitSelector from '../../components/common/OrgUnitSelector';
import {colors, spacing} from '../../theme';

const TABS = [
  {key: 'active', label: 'Active'},
  {key: 'delayed', label: 'Delayed'},
  {key: 'dueToday', label: 'Due Today'},
  {key: 'dueTomorrow', label: 'Due Tomorrow'},
  {key: 'completed', label: 'Completed'},
];

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const mon = d.toLocaleString('en', {month: 'short'});
  return `${day} ${mon}`;
};

const TasksListScreen = () => {
  const navigation = useNavigation();
  const {selectedOrgUnitId} = useOrgUnitStore();
  const [activeTab, setActiveTab] = useState('active');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState('');
  const scrollRef = useRef(null);
  const tabRefs = useRef({});

  const fetchItems = useCallback(async (tab, orgUnitId) => {
    try {
      const res = await taskApi.getTaskList({tab, orgUnitId});
      setItems(res.data.data || []);
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || 'Failed to load tasks');
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      setLoading(true);
      fetchItems(activeTab, selectedOrgUnitId).finally(() => setLoading(false));
    }, [activeTab, selectedOrgUnitId, fetchItems]),
  );

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchItems(activeTab, selectedOrgUnitId);
    setRefreshing(false);
  }, [activeTab, selectedOrgUnitId, fetchItems]);

  const handleTabPress = useCallback((key) => {
    setActiveTab(key);
    const ref = tabRefs.current[key];
    if (ref && scrollRef.current) {
      ref.measureLayout(
        scrollRef.current.getInnerViewRef(),
        (x) => {
          scrollRef.current.scrollTo({x: Math.max(0, x - 20), animated: true});
        },
        () => {},
      );
    }
  }, []);

  const handleCardPress = useCallback((item) => {
    navigation.navigate('TaskManagement', {
      instanceId: item.instanceId,
      orderId: item.orderId,
      instanceCode: item.instanceCode,
      orderNumber: item.orderNumber,
      customerName: item.customerName,
      catalogItemName: item.catalogItemName,
      deliveryDate: item.deliveryDate,
      unitPrice: item.unitPrice,
      expectedCompletionDate: item.expectedCompletionDate,
      clothImageUrl: item.clothImageUrl,
    });
  }, [navigation]);

  const renderTimeline = (tasks, item) => {
    if (!tasks || tasks.length === 0) return null;

    const allCompleted = tasks.every(t => t.status === 'COMPLETED');

    return (
      <View style={styles.timeline}>
        {tasks.map((task, idx) => {
          const isCompleted = task.status === 'COMPLETED';
          const isInProgress = task.status === 'IN_PROGRESS';
          const isAssigned = task.status === 'ASSIGNED';
          const circleColor = isCompleted
            ? colors.taskStatus.COMPLETED
            : isInProgress
              ? colors.taskStatus.IN_PROGRESS
              : isAssigned
                ? colors.taskStatus.ASSIGNED
                : colors.taskStatus.PENDING;

          return (
            <View key={task.id} style={styles.timelineItem}>
              {idx > 0 && (
                <View
                  style={[
                    styles.timelineLine,
                    isCompleted && styles.timelineLineCompleted,
                  ]}
                />
              )}
              <View style={[styles.timelineCircle, {backgroundColor: circleColor}]}>
                {isCompleted && (
                  <MaterialCommunityIcons name="check" size={10} color={colors.white} />
                )}
              </View>
              <Text style={styles.timelineLabel} numberOfLines={1}>
                {task.taskTypeName}
              </Text>
              {task.scheduledDate && (
                <Text style={styles.timelineDate}>{formatDate(task.scheduledDate)}</Text>
              )}
            </View>
          );
        })}

        {/* Virtual step: Expected Completion */}
        <View style={styles.timelineItem}>
          <View
            style={[
              styles.timelineLine,
              allCompleted && styles.timelineLineCompleted,
            ]}
          />
          <View style={[styles.timelineCircle, {backgroundColor: allCompleted ? colors.taskStatus.COMPLETED : colors.border}]}>
            <MaterialCommunityIcons name="flag-checkered" size={10} color={allCompleted ? colors.white : colors.textSecondary} />
          </View>
          <Text style={styles.timelineLabel} numberOfLines={1}>Complete</Text>
          {item.expectedCompletionDate && (
            <Text style={styles.timelineDate}>{formatDate(item.expectedCompletionDate)}</Text>
          )}
        </View>

        {/* Virtual step: Expected Delivery */}
        <View style={styles.timelineItem}>
          <View
            style={[
              styles.timelineLine,
              allCompleted && styles.timelineLineCompleted,
            ]}
          />
          <View style={[styles.timelineCircle, {backgroundColor: allCompleted ? colors.taskStatus.COMPLETED : colors.border}]}>
            <MaterialCommunityIcons name="truck-delivery-outline" size={10} color={allCompleted ? colors.white : colors.textSecondary} />
          </View>
          <Text style={styles.timelineLabel} numberOfLines={1}>Delivery</Text>
          {item.deliveryDate && (
            <Text style={styles.timelineDate}>{formatDate(item.deliveryDate)}</Text>
          )}
        </View>
      </View>
    );
  };

  const renderItem = ({item}) => (
    <TouchableOpacity
      style={styles.card}
      onPress={() => handleCardPress(item)}
      activeOpacity={0.7}>
      <View style={styles.cardTop}>
        <View style={styles.cardAvatar}>
          {item.clothImageUrl ? (
            <Image
              source={{uri: item.clothImageUrl}}
              style={styles.avatarImage}
              resizeMode="cover"
            />
          ) : (
            <MaterialCommunityIcons name="tshirt-crew-outline" size={28} color={colors.textSecondary} />
          )}
        </View>
        <View style={styles.cardInfo}>
          <Text style={styles.customerName} numberOfLines={1}>
            {item.customerName || 'Unknown'}
          </Text>
          <Text style={styles.catalogItem} numberOfLines={1}>
            {item.catalogItemName || 'N/A'}
          </Text>
          <Text style={styles.instanceCode} numberOfLines={1}>
            {item.instanceCode}
          </Text>
          <View style={styles.badgeRow}>
            {item.delayed ? (
              <View style={[styles.badge, styles.badgeDelayed]}>
                <Text style={styles.badgeText}>Delayed</Text>
              </View>
            ) : (
              <View style={[styles.badge, styles.badgeOnTime]}>
                <Text style={styles.badgeText}>On Time</Text>
              </View>
            )}
            {item.deliveryDate && (
              <Text style={styles.deliveryDate}>
                Delivery: {formatDate(item.deliveryDate)}
              </Text>
            )}
          </View>
        </View>
      </View>
      {renderTimeline(item.tasks, item)}
    </TouchableOpacity>
  );

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.Action
          icon="menu"
          onPress={() => navigation.dispatch(DrawerActions.openDrawer())}
        />
        <Appbar.Content title="Tasks List" />
        <OrgUnitSelector />
      </Appbar.Header>

      <View style={styles.tabBarContainer}>
        <ScrollView
          ref={scrollRef}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.tabBarContent}>
          {TABS.map(tab => {
            const isActive = activeTab === tab.key;
            return (
              <TouchableOpacity
                key={tab.key}
                ref={ref => { tabRefs.current[tab.key] = ref; }}
                onPress={() => handleTabPress(tab.key)}
                style={[styles.tab, isActive && styles.tabActive]}
                activeOpacity={0.7}>
                <Text
                  style={[
                    styles.tabLabel,
                    isActive ? styles.tabLabelActive : styles.tabLabelInactive,
                  ]}>
                  {tab.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </View>

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <FlatList
          data={items}
          keyExtractor={item => String(item.instanceId)}
          renderItem={renderItem}
          contentContainerStyle={styles.listContent}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              colors={[colors.primary]}
            />
          }
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <MaterialCommunityIcons
                name="clipboard-check-outline"
                size={48}
                color={colors.textLight}
              />
              <Text style={styles.emptyText}>No tasks found</Text>
            </View>
          }
        />
      )}

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  tabBarContainer: {
    backgroundColor: colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  tabBarContent: {paddingHorizontal: spacing.xs},
  tab: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 2,
    borderBottomWidth: 3,
    borderBottomColor: 'transparent',
  },
  tabActive: {borderBottomColor: colors.primary},
  tabLabel: {fontSize: 14, fontWeight: '600'},
  tabLabelActive: {color: colors.primary},
  tabLabelInactive: {color: colors.textSecondary},
  listContent: {padding: spacing.sm},
  card: {
    backgroundColor: colors.surface,
    borderRadius: 10,
    marginBottom: spacing.sm,
    padding: spacing.md,
    elevation: 1,
  },
  cardTop: {flexDirection: 'row', marginBottom: spacing.sm},
  cardAvatar: {
    width: 56,
    height: 56,
    borderRadius: 8,
    backgroundColor: colors.border,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
    overflow: 'hidden',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
  },
  cardInfo: {flex: 1},
  customerName: {fontSize: 15, fontWeight: '600', color: colors.text},
  catalogItem: {fontSize: 13, color: colors.textSecondary, marginTop: 2},
  instanceCode: {fontSize: 12, color: colors.primary, fontWeight: '600', marginTop: 1},
  badgeRow: {flexDirection: 'row', alignItems: 'center', marginTop: 4, gap: 8},
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  badgeOnTime: {backgroundColor: colors.taskStatus.COMPLETED + '20'},
  badgeDelayed: {backgroundColor: colors.error + '20'},
  badgeText: {fontSize: 11, fontWeight: '600'},
  deliveryDate: {fontSize: 12, color: colors.textSecondary},
  timeline: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingTop: spacing.xs,
    borderTopWidth: 1,
    borderTopColor: colors.divider,
  },
  timelineItem: {
    alignItems: 'center',
    flex: 1,
    position: 'relative',
  },
  timelineLine: {
    position: 'absolute',
    top: 8,
    left: '-50%',
    right: '50%',
    height: 2,
    backgroundColor: colors.border,
    zIndex: -1,
  },
  timelineLineCompleted: {backgroundColor: colors.taskStatus.COMPLETED},
  timelineCircle: {
    width: 18,
    height: 18,
    borderRadius: 9,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 4,
  },
  timelineLabel: {fontSize: 10, color: colors.textSecondary, textAlign: 'center'},
  timelineDate: {fontSize: 9, color: colors.textLight, textAlign: 'center'},
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 100,
  },
  emptyText: {
    color: colors.textSecondary,
    marginTop: spacing.sm,
    fontSize: 14,
  },
});

export default TasksListScreen;
